import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';

import {
  closeWidget,
  setVisualViewportOffset,
  setWindowDimensions,
} from 'actions/main';

import type { HandoffStatus } from 'reducers/workflows';
import {
  selectConversationId,
  selectIsMobileSDK,
  selectIsProactiveChatVisible,
  setForethoughtLiveChatStepSent,
  setWindowUpdatedContextVariables,
  showProactivePrompt,
} from 'reducers/workflows';
import {
  clearConversation,
  handleClickOutsideIframe,
  handleSendMessage,
  sendConversationAlert,
  updateHelpdeskConversation,
} from 'actions/workflows-v2/workflowsV2';
import {
  sendTrackingEvent,
  setDidUserCloseProactiveChat,
  setTriggerEventPayloadContextVariables,
  setTriggerEventPayloadEventName,
  updateCustomConfigParams,
} from 'actions/workflows/workflows';
import { isEventTrusted, postMessage } from '../../utils/postMessage';
import { sendResizeiframeMessage } from './utils';
import {
  useHandleProactiveTriggers,
  useHandleWorkflowTriggerOnClick,
} from './hooks';
import { disablePersistence } from 'persistence/persistenceBetweenTabs';
import {
  setAiInstructionsForPreview,
  setIntentFiltersForPreview,
  setPreviewIsLiveMode,
} from 'actions/config/config';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';
import { setHandoffStatusAndErrorMessage } from 'reducers/workflows/index';
import { scrollToTheBottom } from 'utils/helpers';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

export const usePostMessageListener = (): void => {
  const dispatch = useAppDispatch();
  const handleWorkflowTriggerOnClick = useHandleWorkflowTriggerOnClick();
  const handleProactiveTriggers = useHandleProactiveTriggers();
  const isProactiveChatVisible = useAppSelector(selectIsProactiveChatVisible);
  const isMobileSDK = useAppSelector(selectIsMobileSDK);
  const conversationId = useAppSelector(selectConversationId);

  const handoffSolveCallback = useCallback(
    (success: boolean, hideWidget = true) => {
      if (!success) {
        // On handoff failure, ensure the widget remains visible.
        postMessage({ event: 'showSolveWidget' });
        return;
      }

      if (hideWidget) {
        if (isMobileSDK) {
          // On mobile SDK, do not close the widget, but trigger a resize event for iOS modal handling.
          sendResizeiframeMessage(false);
        } else {
          // On desktop, hide the widget after successful handoff.
          postMessage({ event: 'hideSolveWidget' });
        }
      }
    },
    [isMobileSDK],
  );

  const handoffWorkflowsCallback = useCallback(
    (success: boolean, handoffFailureMessage?: string) => {
      const status: HandoffStatus = success ? 'success' : 'failure';

      dispatch(
        setHandoffStatusAndErrorMessage({
          handoffFailureMessage: handoffFailureMessage,
          handoffStatus: status,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      if (!isEventTrusted(e)) {
        return;
      }

      if (e.data.action === 'openWidget') {
        handleWorkflowTriggerOnClick();

        dispatch(
          sendTrackingEvent({
            event_type: 'widget-opened',
            mechanism: e.data.mechanism,
          }),
        );
      }
      if (e.data.action === 'closeWidget') {
        dispatch(closeWidget());
      }
      if (e.data.action === 'showIframe') {
        // Have to use a non-zero timeout.
        // Otherwise, iframe size will be calculated incorrectly.
        setTimeout(() => {
          postIframeResizeEvent();
        }, 1000);
      }
      if (e.data.action === 'showIframeOpen') {
        scrollToTheBottom('instant');
        // Have to call scrollToTheBottom() AGAIN after a timeout, because the
        // first call doesn't scroll to the very bottom (a few pixels short).
        setTimeout(() => {
          scrollToTheBottom('instant');
        }, 100);
      }
      if (e.data.event === 'handoffStatus') {
        handoffWorkflowsCallback(e.data.success, e.data.handoffFailureMessage);
        handoffSolveCallback(e.data.success, e.data.hideWidget);
      }
      if (e.data.event === 'noZendeskObjectDetected') {
        sendErrorToSentry({
          consoleError: true,
          conversationId: conversationId,
          error: new Error('window.zE is undefined'),
        });
      }
      if (e.data.event === 'triggerEventComplete') {
        const payload = e.data.payload || {};
        const eventName = e.data.identifier || '';
        dispatch(setTriggerEventPayloadContextVariables({ ...payload }));
        dispatch(setTriggerEventPayloadEventName(eventName));
      }
      if (e.data.event === 'updateIntentFiltersForPreview') {
        dispatch(setIntentFiltersForPreview(e.data.intentFiltersForPreview));
        dispatch(setPreviewIsLiveMode(e.data.isLiveMode));
      }
      if (e.data.event === 'updateAiInstructionFiltersForPreview') {
        dispatch(
          setAiInstructionsForPreview(e.data.aiInstructionFiltersForPreview),
        );
      }
      if (e.data.event === 'handoffTrackingEvent') {
        dispatch(
          sendTrackingEvent({
            ...e.data.handoffData,
            event_type: 'widget-customer-handoff',
          }),
        );
      }
      if (e.data.event === 'zendeskUpdateSettings') {
        dispatch(
          sendTrackingEvent({
            ...e.data.updateSettingsData,
            event_type: 'widget-zendesk-update-settings',
          }),
        );
      }
      if (e.data.action === 'clearLocalData') {
        dispatch(
          clearConversation({
            reason: 'clear_local_data',
          }),
        );
        dispatch(disablePersistence());
      }
      if (e.data.action === 'launchQuery') {
        const initialQuery = e.data.payload?.query;
        if (initialQuery) {
          dispatch(clearConversation({ initialQuery, reason: 'launch_query' }));
          handleWorkflowTriggerOnClick({ initialQueryParam: initialQuery });
          dispatch(
            sendTrackingEvent({
              event_type: 'widget-opened',
              mechanism: 'js-api-widget-launchQuery',
            }),
          );
        }
      }
      if (e.data.event === 'forethoughtSetVisualViewport') {
        dispatch(setVisualViewportOffset(e.data.payload));
      }
      if (e.data.event === 'updateHelpdeskConversation') {
        dispatch(
          updateHelpdeskConversation({
            forethought_conversation_id: e.data.forethoughtConversationID,
            helpdesk_conversation_id: e.data.helpdeskConversationId,
            include_attachments: e.data.includeAttachments,
            include_transcript: e.data.includeTranscript,
            integration: e.data.integration,
            step_type: e.data.stepType,
          }),
        );
      }
      if (e.data.action === 'showProactivePrompt') {
        if (isProactiveChatVisible) {
          return;
        }

        postMessage({ event: 'forethoughtWidgetResizeToMaximumValues' });
        dispatch(showProactivePrompt());
        dispatch(
          sendTrackingEvent({
            event_type: 'widget-customer-proactive-bubbles-shown',
          }),
        );
      }
      if (e.data.action === 'hideProactivePrompt') {
        dispatch(setDidUserCloseProactiveChat(true));
      }
      if (e.data.event === 'forethoughtSendTrackingEvent') {
        dispatch(sendTrackingEvent(e.data.trackingEvent));
      }
      if (e.data.event === 'forethoughtLiveChatSetStepSent') {
        dispatch(setForethoughtLiveChatStepSent());
      }
      if (e.data.event === 'forethoughtSendConversationAlert') {
        dispatch(sendConversationAlert(e.data.requestData));
      }
      if (e.data.event === 'forethoughtClickOutsideWidget') {
        dispatch(handleClickOutsideIframe());
      }
      if (e.data.event === 'updateConversationContext') {
        dispatch(setWindowUpdatedContextVariables(e.data.payload));
      }
      if (e.data.event === 'updateConfigParams') {
        dispatch(updateCustomConfigParams(e.data.payload));
      }
      if (e.data.action === 'proactiveTrigger') {
        const { payload } = e.data;
        const { eventName } = payload;
        handleProactiveTriggers(eventName);
      }
      if (e.data.event === 'forethoughtWindowResize') {
        dispatch(setWindowDimensions(e.data.windowDimensions));
      }
      if (e.data.action === 'sendMessage') {
        const message = e.data.payload?.message;
        if (message) {
          dispatch(handleSendMessage({ message }));
        }
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // Method gets updated
  }, [
    conversationId,
    dispatch,
    handleProactiveTriggers,
    handleWorkflowTriggerOnClick,
    handoffSolveCallback,
    handoffWorkflowsCallback,
    isProactiveChatVisible,
  ]);
};
